import qs from 'qs'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import useSWR from 'swr'
import { AggregatedAlertsOverTimeBasis } from '@/types/reports'
import { ALERT_STATUS } from '@saasalerts/common/shared'
import { momentUTC } from '@/utils/momentUTC'
import AppLayout from '@/components/Layout/Layout'
import {
  formatEndDateToTimezone,
  formatStartDateToTimezone,
  formatToComputerDate,
  formatToLocalComputerDate,
} from '@/utils/methods'
import { Event, LabelCount, ProductEventActivity } from '@/components/Graphs'
import {
  RiskDashboardCharts,
  RiskDashboardSelectOption,
} from '@/components/Pages/RiskDashboard'
import { ReportHeader } from '@/components/Layout/Report'
import moment from 'moment'
import { DateRangePicker } from '@saasalerts/ui'
import { Organization } from '@saasalerts/common/manage'
import { CardGrid } from '@saasalerts/ui'
import CardLoader from '@/components/Basic/CardLoader'
import { dateRangeQuickSelectOptions } from '@/utils/dateRangeQuickSelectOptions'

/* eslint-disable react-hooks/rules-of-hooks */

type RiskDashboardFilters = {
  startDate: string
  endDate: string
}

const Report = ({
  filters,
  organizations: organizations,
}: {
  filters?: RiskDashboardFilters
  organizations?: Array<Organization>
}) => {
  if (filters === undefined) {
    return null
  }

  if (organizations === undefined) {
    return <CardLoader />
  }

  const { register, watch } = useForm<RiskDashboardSelectOption>({
    defaultValues: {
      countryAlertStatus: ALERT_STATUS.CRITICAL,
      organizationAlertStatus: ALERT_STATUS.CRITICAL,
      accountAlertStatus: ALERT_STATUS.CRITICAL,
      productAlertStatus: ALERT_STATUS.CRITICAL,
    },
  })
  const countryAlertStatus = watch('countryAlertStatus')
  const organizationAlertStatus = watch('organizationAlertStatus')
  const accountAlertStatus = watch('accountAlertStatus')
  const productAlertStatus = watch('productAlertStatus')

  const dateFiltersQuery = {
    startDate: formatStartDateToTimezone(filters.startDate),
    endDate: formatEndDateToTimezone(filters.endDate),
  }

  const { data: criticalAlertsByCountry, error: criticalAlertsByCountryError } =
    useSWR<Array<LabelCount>>(
      `/api/organizations/critical-alerts-by-country?${qs.stringify({
        ...dateFiltersQuery,
        alertStatus: countryAlertStatus,
      })}`
    )

  const {
    data: potentialRisks,
    error: potentialRiskError,
    isValidating,
  } = useSWR<Array<Event>>(`/api/organizations/live-potential-risks`, {
    refreshInterval: 10000,
  })

  const { data: organizationAlerts, error: organizationError } = useSWR<
    Array<LabelCount>
  >(
    `/api/organizations/aggregated-alerts-over-time?${qs.stringify({
      ...dateFiltersQuery,
      alertStatus: organizationAlertStatus,
      aggregatedAlertsOverTimeBasis: AggregatedAlertsOverTimeBasis.ORGANIZATION,
    })}`
  )

  const { data: accountAlerts, error: accountError } = useSWR<
    Array<LabelCount>
  >(
    `/api/organizations/aggregated-alerts-over-time?${qs.stringify({
      ...dateFiltersQuery,
      alertStatus: accountAlertStatus,
      aggregatedAlertsOverTimeBasis: AggregatedAlertsOverTimeBasis.ACCOUNT,
    })}`
  )

  const {
    data: organizationsExternalFileShareEvents,
    error: organizationsExternalFileShareEventsError,
  } = useSWR<Array<LabelCount>>(
    `/api/organizations/organizations-external-file-share-events?${qs.stringify(
      dateFiltersQuery
    )}`
  )

  const {
    data: productWiseEventActivities,
    error: productWiseEventActivitiesError,
  } = useSWR<Array<ProductEventActivity>>(
    `/api/organizations/product-wise-event-activity?${qs.stringify({
      ...dateFiltersQuery,
      alertStatus: productAlertStatus,
    })}`
  )

  return (
    <RiskDashboardCharts
      data={{
        startDate: filters.startDate,
        endDate: filters.endDate,

        criticalAlertsByCountry: {
          data: criticalAlertsByCountry,
          error: criticalAlertsByCountryError?.response?.error,
        },
        potentialRisks: {
          data: potentialRisks,
          isValidatingPotentialRisks: isValidating,
          error: potentialRiskError?.response?.error,
        },
        organizationAlerts: {
          data: organizationAlerts,
          error: organizationError?.response?.error,
        },
        accountAlerts: {
          data: accountAlerts,
          error: accountError?.response?.error,
        },
        organizationsExternalFileShareEvents: {
          data: organizationsExternalFileShareEvents,
          error: organizationsExternalFileShareEventsError?.response?.error,
        },
        productWiseEventActivities: {
          data: productWiseEventActivities,
          error: productWiseEventActivitiesError?.response?.error,
        },
        organizations,
      }}
      register={register}
      watch={watch}
    />
  )
}
export default function Home(): React.ReactElement {
  const { data: organizations } =
    useSWR<Array<Organization>>('/api/organizations')

  const defaultValues = {
    startDate: formatToComputerDate(momentUTC().subtract(1, 'week')),
    endDate: formatToComputerDate(momentUTC()),
  }

  const [filters, setFilters] = useState<RiskDashboardFilters>(defaultValues)

  return (
    <CardGrid columns={1} containerPadding={4}>
      <div className="flex flex-wrap justify-between md:flex-nowrap lg:flex-nowrap print:hidden">
        <ReportHeader heading="Risk Dashboard" />
        <div className="pt-2 sm:pt-0 print:hidden">
          <DateRangePicker
            mode="range-date"
            showSidebar
            showRelativeTime={false}
            blockFutureDates
            quickSelectOptions={dateRangeQuickSelectOptions}
            value={{
              start: filters.startDate
                ? moment(filters.startDate).toDate()
                : null,
              end: filters.endDate ? moment(filters.endDate).toDate() : null,
            }}
            onChange={(range) => {
              if (range.start && range.end) {
                setFilters({
                  startDate: formatToLocalComputerDate(range.start),
                  endDate: formatToLocalComputerDate(range.end),
                })
              }
            }}
            label="Date Range"
          />
        </div>
      </div>
      <Report filters={filters} organizations={organizations} />
    </CardGrid>
  )
}

Home.Layout = AppLayout
